import { interpolate } from 'd3-interpolate'
import * as geo from 'd3-geo'
import { get as getProjection} from '..'
import Transformer from './transformer'

export class HighLowTransformer extends Transformer {

  constructor (enter, exit, opts) {
    super(enter, exit, opts)

    // Make sure the enter data has the correct data
    let enterData = enter.datum()
    enterData.projection = getProjection(enterData.node, enterData.geojson, opts)
    enterData.path = geo.geoPath().projection(enterData.projection)

    let {low, high} = this.resolutions(enter, exit)

    this.high = high
    this.low = low
    this.zoomOut = opts.zoomOut

    this.configureHighRes(high, low.datum(), opts)
    this.configureLowRes(low, high.datum(), opts)
  }

  /*
   * Configures the low resolution map objects.
   */
  configureLowRes (low, highData, opts) {
    let lowData = low.datum()
      , lowGeo = this._buildFeatureCollection(lowData.zoomedTo, lowData.geojson)

    // Set the fit size on the projection
    lowData.projection.fitSize([opts.width, opts.height], lowGeo)

    // Grab the high res feature within the low res map
    this.lowRes = this.low.selectAll('path').filter(feature => feature.id == highData.id)

    let lowResEndJson = highData.geojson
      , lowResEndPath = geo.geoPath().projection(getProjection(highData.node, lowResEndJson, opts))

    lowResEndPath.projection()
                 .precision(Number.POSITIVE_INFINITY)

    if (this.lowRes.size()) {
      // The high res image (e.g. the usa) is an element of the new low res
      // We need to interpolate
      let lowRes0 = lowData.path(this.lowRes.datum())
        , lowRes1 = lowResEndPath(this.lowRes.datum())

      this.lowResInterpolater = interpolate(lowRes0, lowRes1)
    }

    // Determine the scales of the low feature
    let projection = getProjection(lowData.node, lowGeo, opts)
      , scale0 = projection.scale()
      , translate0 = projection.translate()

    // Now blow up the low res projection so it's focused on the high
    // res feature
    projection.fitSize([opts.width, opts.height], {
      type: 'FeatureCollection',
      features: this.lowZoomed(highData)
    })

    this.lowInterpolaters = {
      scale: interpolate(scale0, projection.scale()),
      translate: interpolate(translate0, projection.translate())
    }

    this.lowPath = lowData.path
  }

  /*
   * Configures the transformer's high resolution objects. The high
   * resolution feature's final projection is fit to its geojson. The feature
   * starts using a projection based on the the low resolution map zoomed
   */
  configureHighRes (high, lowData, opts) {
    let highData = high.datum()
      , lowGeo = this._buildFeatureCollection(lowData.zoomedTo, lowData.geojson)
      , lowProjection = getProjection(lowData.node, lowGeo, opts)
      , lowPath = geo.geoPath().projection(lowProjection)

    highData.projection.fitSize([opts.width, opts.height], highData.geojson)

    high.selectAll('path')
        .each(function (d) {
          // Attach an interpolater on each new path that uses
          // the previous map's projection as the start and the new high
          // path as the end
          this._tween = interpolate(lowPath(d), highData.path(d))
        })
  }

  /*
   * Return the feature within the high res image
   * that the low resolution will zoom to
   */
  lowZoomed (high) {
    // By default just return the high resolution features
    return high.geojson.features
  }

  transform (t) {
    if (this.zoomOut) {
      // Reverse the transformation
      t = 1 - t
    }

    this.high
        .selectAll('path')
        .attr('d', function () {
          return this._tween(t)
        })

    this.low
        .datum().projection
        .scale(this.lowInterpolaters.scale(t))
        .translate(this.lowInterpolaters.translate(t))

    this.low
        .selectAll('path')
        .attr('d', d => this.lowPath(d))

    if (this.lowResInterpolater) {
      this.lowRes.attr('d', this.lowResInterpolater(t))
    }
  }

}
